<template>
  <div
    :id="`${source}_call_participants`"
    class="flex flex-col w-full gap-1 proxima_nova text_color"
  >
    <SkeletonLoader v-if="isLoading" :page="loaderPages.CALL_PARTICIPANTS" />

    <div
      v-else
      v-for="(participant, index) in visibleParticipants"
      :key="`${participant.id || participant.callSid || participant.call_sid}_participant_${index}`"
      class="flex flex-row items-center justify-between py-1"
    >
      <div class="flex flex-row items-center gap-3">
        <ProfileIcon :data="participant" />

        <div class="flex flex-col gap-1">
          <span class="text-sm">{{ participantTitle(participant) }}</span>
          <span
            v-if="!isMerged && participant?.hold"
            class="text-[11px] opacity-60"
            >On hold</span
          >
          <span v-else-if="isMerged" class="text-[11px] opacity-60"
            >In call</span
          >
        </div>
      </div>

      <a-button
        v-if="participant.label !== conferenceParticipantLabels.CALLER"
        class="end_call w-9 p-0"
        @click="toConferenceKickParticipant(participant)"
      >
        <ThemeImage
          class="size-4 rotate-[135deg]"
          :icon="imageNames.CALL_UNSELECTED"
          alt="end_call_icon"
        />
      </a-button>
    </div>
  </div>
</template>

<script setup>
import { computed } from "vue";
import { renderEntity } from "@/src/utils/utils.js";
import { loaderPages } from "@/src/constants/loaderConstants.js";
import { conferenceParticipantLabels } from "@/src/constants/callConstants.js";
import store from "@/src/store/index.js";
import CallService from "@/src/api/CallService.js";
import ProfileIcon from "@/src/components/ProfileIcon.vue";
import SkeletonLoader from "@/src/components/SkeletonLoader.vue";
import {imageNames} from "@/src/constants/imageConstants.js";

const props = defineProps({
  source: { type: String, required: true },
});

const participants = computed(
  () => store.getters["conferenceCall/participants"],
);
const isLoading = computed(
  () =>
    store.getters["conferenceCall/transferringCall"] ||
    store.getters["conferenceCall/swappingCalls"],
);

const isMerged = computed(() => store.getters["conferenceCall/isMerged"]);

const participantsOnHold = computed(() =>
  participants.value?.filter((participant) => participant.hold),
);

// In merged mode all 3 legs (caller, original_agent, transfer_target) are
// actively talking, so we surface every participant. Outside merged mode we
// keep the legacy "on hold" filter that drives the "Caller is on hold" UI.
const visibleParticipants = computed(() => {
  if (isMerged.value) {
    return participants.value || [];
  }
  return participantsOnHold.value || [];
});

const participantTitle = computed(() => {
  return (participant) =>
    participant?.name?.trim() ||
    participant?.to ||
    participant?.phone_number ||
    participant?.number ||
    renderEntity(participant);
});

const toConferenceKickParticipant = async (data) => {
  try {
    const payload = {
      ...data,
      conference_id: store.getters["conferenceCall/conferenceSid"],
    };

    const response = await CallService.conferenceKickParticipant(payload);
    if (response?.success) {
      console.log(response);
    }
  } catch (error) {
    console.error(
      "An error occurred while kicking a conference participant: ",
      error,
    );
  }
};
</script>
